import { useState, useEffect, useMemo, useCallback } from 'react';
import { C, iS, PROGRAMS, PROGRAM_MAP, P1, SIDEBAR_FULL, SIDEBAR_MINI, SITE_TITLE, todayStr, makeCode } from './config.js';
import { API } from './api.js';
import { useToast, Btn } from './common.jsx';
import { AddCourseModal } from './CourseModals.jsx';
import DetailPanel from './DetailPanel.jsx';
import ReportPage from './ReportPage.jsx';
import './adm.css';

const BASE = "https://parseapi.back4app.com";

export function GameAdmin({ user, onLogout }) {
  const [page,      setPage]      = useState("courses");
  const [courses,   setCourses]   = useState([]);
  const [codeStats, setCodeStats] = useState({});
  const [loading,   setLoading]   = useState(false);
  const [conn,      setConn]      = useState({ status:"loading", label:"연결 중…" });
  const [sel,       setSel]       = useState(null);
  const [showAdd,   setShowAdd]   = useState(false);
  const [search,    setSearch]    = useState("");
  /* ✅ 4번 — 게임별 탭 */
  const [activeTab, setActiveTab] = useState("all"); // "all" | programId
  const { show: toast, ToastContainer } = useToast();

  const navItems = [
    { key:"courses", label:"전체 과정 관리", icon:"≡" },
    { key:"report",  label:"보고서",         icon:"▣" },
  ];

  useEffect(() => {
    document.title = SITE_TITLE;
    return () => { document.title = "React App"; };
  }, []);

  /* ✅ 5번 — 생성 속도 개선: codeStats를 병렬 처리 + 캐시 */
  const loadCodeStats = useCallback(async (rows) => {
    const stats = {};
    await Promise.all(rows.map(async (c) => {
      const prog = c._prog;
      if (!prog?.codeTable || !c.useDate || !c.round) {
        stats[c.objectId] = { total: c.capacity || 0, used: 0 };
        return;
      }
      try {
        const headers    = { "X-Parse-Application-Id": prog.appId, "X-Parse-REST-API-Key": prog.apiKey || prog.restKey || prog.jsKey };
        const roundAlpha = String.fromCharCode(96 + Number(c.round));
        const whereTotal = encodeURIComponent(JSON.stringify({ useDate: c.useDate, round: roundAlpha }));
        const whereUsed  = encodeURIComponent(JSON.stringify({ useDate: c.useDate, round: roundAlpha, usageCount: { $gte: 1 } }));
        const [resT, resU] = await Promise.all([
          fetch(`${BASE}/classes/${prog.codeTable}?where=${whereTotal}&count=1&limit=0`, { headers }),
          fetch(`${BASE}/classes/${prog.codeTable}?where=${whereUsed}&count=1&limit=0`,  { headers }),
        ]);
        const [dT, dU] = await Promise.all([resT.json(), resU.json()]);
        stats[c.objectId] = { total: dT.count || 0, used: dU.count || 0 };
      } catch {
        stats[c.objectId] = { total: c.capacity || 0, used: 0 };
      }
    }));
    setCodeStats(stats);
  }, []); // eslint-disable-line

  const load = useCallback(async () => {
    setLoading(true);
    setConn({ status:"loading", label:"연결 중…" });
    try {
      const d    = await API.course.list({ limit: 200 });
      const rows = (d.results || []).map(c => ({ ...c, _prog: PROGRAM_MAP[c.programId] || P1 }));
      setCourses(rows);
      setConn({ status:"ok", label:`연결됨 · ${rows.length}개` });
      loadCodeStats(rows);
    } catch {
      setCourses([]);
      setConn({ status:"err", label:"연결 오류" });
    } finally {
      setLoading(false);
    }
  }, [loadCodeStats]); // eslint-disable-line

  useEffect(() => { load(); }, []); // eslint-disable-line

  const selectCourse = (c) => setSel(c);
  const closePanel   = (action) => { setSel(null); if (action === "refresh") load(); };

  // 과정 삭제
  const delCourse = async (c, e) => {
    e.stopPropagation();
    const prog       = c._prog;
    const roundAlpha = String.fromCharCode(96 + Number(c.round));
    const restKey    = prog?.apiKey || prog?.restKey || prog?.jsKey;
    const rH = { "X-Parse-Application-Id": prog?.appId, "X-Parse-REST-API-Key": restKey, "Content-Type": "application/json" };

    // ⚠️ 임시: 사용된 코드가 있어도 삭제 허용 (다시 막으려면 false → true 로 변경)
    const BLOCK_USED_DELETE = false;
    if (BLOCK_USED_DELETE && prog?.codeTable && c.useDate && c.round) {
      try {
        const where = JSON.stringify({ useDate: c.useDate, round: roundAlpha, usageCount: { $gte: 1 } });
        const res   = await fetch(`${BASE}/classes/${prog.codeTable}?where=${encodeURIComponent(where)}&limit=1`, { headers: rH });
        const data  = await res.json();
        if ((data.results || []).length > 0) { toast("코드를 사용한 참여자가 있어 삭제할 수 없습니다.", "warning"); return; }
      } catch {}
    }

    if (!window.confirm(`"${c.courseName}" 과정을 삭제하시겠습니까?\n미사용 코드도 함께 삭제됩니다.`)) return;

    try {
      if (prog?.codeTable && c.useDate && c.round) {
        const where = JSON.stringify({ useDate: c.useDate, round: roundAlpha });
        const res   = await fetch(`${BASE}/classes/${prog.codeTable}?where=${encodeURIComponent(where)}&limit=1000`, { headers: rH });
        const data  = await res.json();
        // ✅ 병렬 삭제
        await Promise.all((data.results || []).map(code =>
          fetch(`${BASE}/classes/${prog.codeTable}/${code.objectId}`, { method:"DELETE", headers: rH })
        ));
      }
      await API.course.remove(c.objectId);
      if (sel?.objectId === c.objectId) setSel(null);
      toast("과정과 미사용 코드가 삭제되었습니다.");
      load();
    } catch (err) {
      toast("삭제 실패: " + err.message, "error");
    }
  };

  /* ✅ 4번 — 게임별 탭 필터 */
  const tabPrograms = [
    { id: "all", name: "전체" },
    ...PROGRAMS,
  ];

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return courses.filter(c => {
      const matchTab    = activeTab === "all" || c.programId === activeTab;
      const matchSearch = !q || (c.courseName||"").toLowerCase().includes(q) || (c.program||"").toLowerCase().includes(q);
      return matchTab && matchSearch;
    });
  }, [courses, search, activeTab]);

  const panelOpen = !!sel;
  const dotColor  = conn.status==="ok" ? C.accent : conn.status==="err" ? C.danger : "#f59e0b";

  return (
    <div style={{ display:"grid", gridTemplateRows:"52px 1fr", height:"100vh", fontFamily:"'Pretendard','Noto Sans KR',-apple-system,sans-serif", color:C.t1, fontSize:14, background:C.bg }}>

      {/* ── TOPBAR ── */}
      <div style={{ background:C.surface, borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", padding:"0 22px", gap:12, boxShadow:"0 2px 8px rgba(92,124,206,.07)" }}>
        <span style={{ fontSize:15, fontWeight:700, color:C.t1, letterSpacing:"-.3px" }}>
          <em style={{ color:C.accent, fontStyle:"normal" }}>Game</em> 참여인원 관리자
        </span>
        <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:10 }}>
          {user && <span style={{ fontSize:12, color:C.t2, fontWeight:600 }}>👤 {user.get("username")}</span>}
          <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:C.t3, padding:"5px 12px", borderRadius:20, background:C.bg, border:`1px solid ${C.border}` }}>
            <div style={{ width:6, height:6, borderRadius:"50%", background:dotColor, boxShadow:conn.status==="ok"?`0 0 6px ${C.accent}`:"none" }}/>
            {conn.label}
          </div>
          <button onClick={load} title="새로고침"
            style={{ width:32, height:32, borderRadius:8, border:`1px solid ${C.border}`, background:C.surface, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:16, color:C.t3 }}
            onMouseEnter={e => { e.currentTarget.style.background=C.accentBg; e.currentTarget.style.color=C.accent; }}
            onMouseLeave={e => { e.currentTarget.style.background=C.surface;  e.currentTarget.style.color=C.t3; }}
          >↻</button>
          {onLogout && (
            <button onClick={onLogout}
              style={{ padding:"6px 14px", borderRadius:8, border:`1px solid ${C.border}`, background:C.surface, fontSize:12, fontWeight:600, color:C.t2, cursor:"pointer", fontFamily:"inherit", transition:"all .15s" }}
              onMouseEnter={e => { e.currentTarget.style.background=C.dangerBg; e.currentTarget.style.color=C.danger; e.currentTarget.style.borderColor="#fdc5ce"; }}
              onMouseLeave={e => { e.currentTarget.style.background=C.surface;  e.currentTarget.style.color=C.t2;     e.currentTarget.style.borderColor=C.border; }}
            >로그아웃</button>
          )}
        </div>
      </div>

      {/* ── BODY ── */}
      <div style={{ display:"grid", gridTemplateColumns:`${panelOpen ? SIDEBAR_MINI : SIDEBAR_FULL}px 1fr`, overflow:"hidden", transition:"grid-template-columns .28s cubic-bezier(.4,0,.2,1)" }}>

        {/* SIDEBAR */}
        <div style={{ background:C.surface, borderRight:`1px solid ${C.border}`, display:"flex", flexDirection:"column", overflow:"hidden" }}>
          {panelOpen ? (
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", padding:"14px 0", gap:6 }}>
              {navItems.map(item => {
                const active = page === item.key;
                return (
                  <div key={item.key} onClick={() => { setPage(item.key); setSel(null); }} title={item.label}
                    style={{ width:36, height:36, borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:16, background:active?C.accentBg:"transparent", color:active?C.accentTxt:C.t3, transition:"all .15s" }}
                    onMouseEnter={e => { if(!active) e.currentTarget.style.background=C.accentBg; }}
                    onMouseLeave={e => { if(!active) e.currentTarget.style.background="transparent"; }}
                  >{item.icon}</div>
                );
              })}
            </div>
          ) : (
            <div style={{ padding:"18px 12px", display:"flex", flexDirection:"column", gap:2, flex:1 }}>
              <div style={{ fontSize:12, fontWeight:700, color:C.t3, letterSpacing:"1.2px", padding:"10px 10px 5px", textTransform:"uppercase" }}>Menu</div>
              {navItems.map(item => {
                const active = page === item.key;
                return (
                  <div key={item.key} onClick={() => setPage(item.key)}
                    style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 12px", borderRadius:10, cursor:"pointer", fontSize:13, fontWeight:active?600:500, color:active?"#fff":C.t2, background:active?C.accent:"transparent", boxShadow:active?"0 4px 12px rgba(92,124,206,.28)":"none", transition:"all .15s", whiteSpace:"nowrap" }}
                    onMouseEnter={e => { if(!active){ e.currentTarget.style.background=C.accentBg; e.currentTarget.style.color=C.t1; } }}
                    onMouseLeave={e => { if(!active){ e.currentTarget.style.background="transparent"; e.currentTarget.style.color=C.t2; } }}
                  >
                    <span>{item.icon}</span>
                    {item.label}
                    {item.key==="courses" && (
                      <span style={{ marginLeft:"auto", fontSize:12, fontWeight:700, padding:"2px 8px", borderRadius:20, background:active?"rgba(255,255,255,.28)":C.bg, color:active?"#fff":C.t3 }}>{courses.length}</span>
                    )}
                  </div>
                );
              })}
              <div style={{ marginTop:"auto", padding:"12px 10px 2px", borderTop:`1px solid ${C.border}` }}>
                <div style={{ fontSize:12, color:C.t3, fontWeight:700, letterSpacing:"1px", marginBottom:4 }}>PROGRAMS</div>
                <div style={{ fontSize:12, fontWeight:700, color:C.accentTxt }}>{PROGRAMS.map(p=>p.name).join(" / ")}</div>
              </div>
            </div>
          )}
        </div>

        {/* MAIN CONTENT */}
        <div style={{ position:"relative", overflow:"hidden" }}>
          <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", opacity:panelOpen?0:1, pointerEvents:panelOpen?"none":"auto", transition:"opacity .2s ease", background:C.bg }}>

            {page === "courses" && (
              <>
                {/* 헤더 */}
                <div style={{ padding:"20px 24px 0", display:"flex", alignItems:"flex-end", justifyContent:"space-between" }}>
                  <div>
                    <div style={{ fontSize:20, fontWeight:800, color:C.t1, letterSpacing:"-.5px" }}>전체 과정 관리</div>
                    <div style={{ fontSize:12, color:C.t3, marginTop:4 }}>과정을 클릭하면 참여자 목록을 확인할 수 있습니다</div>
                  </div>
                  <Btn onClick={() => setShowAdd(true)}>+ 과정 추가</Btn>
                </div>

                {/* ✅ 4번 — 게임별 탭 */}
                <div style={{ padding:"12px 24px 0", display:"flex", gap:6 }}>
                  {tabPrograms.map(p => {
                    const cnt    = p.id === "all" ? courses.length : courses.filter(c => c.programId === p.id).length;
                    const active = activeTab === p.id;
                    return (
                      <button key={p.id} onClick={() => setActiveTab(p.id)}
                        style={{
                          padding:"6px 14px", borderRadius:20, fontSize:12, fontWeight:600,
                          cursor:"pointer", border:`1.5px solid ${active ? C.accent : C.border}`,
                          background: active ? C.accent : C.surface,
                          color:      active ? "#fff"   : C.t2,
                          transition: "all .15s",
                        }}
                      >
                        {p.name} <span style={{ opacity:.7 }}>{cnt}</span>
                      </button>
                    );
                  })}
                </div>

                {/* 검색 */}
                <div style={{ padding:"12px 24px 10px" }}>
                  <div style={{ position:"relative", maxWidth:300 }}>
                    <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", color:C.t3, pointerEvents:"none" }}>🔍</span>
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="과정명, 프로그램 검색…" style={{ ...iS, paddingLeft:36 }}/>
                  </div>
                </div>

                {/* 테이블 */}
                <div style={{ padding:"0 24px", flex:1, overflowY:"auto" }}>
                  <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, overflow:"hidden", boxShadow:"0 2px 8px rgba(92,124,206,.07)" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse" }}>
                      <thead style={{ background:"#f8fcfa" }}>
                        <tr>
                          {["No","과정명","프로그램","코드 수","일자","회차","시간",""].map(h => (
                            <th key={h} style={{ padding:"11px 16px", textAlign:"left", fontSize:12, fontWeight:700, color:C.t3, letterSpacing:".4px", borderBottom:`1px solid ${C.border}`, whiteSpace:"nowrap" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr><td colSpan={8} style={{ padding:"3rem", textAlign:"center", color:C.t3, fontSize:13 }}>불러오는 중…</td></tr>
                        ) : filtered.length === 0 ? (
                          <tr><td colSpan={8} style={{ padding:"3rem", textAlign:"center", color:C.t3, fontSize:13 }}>과정이 없습니다.</td></tr>
                        ) : filtered.map((c, i) => (
                          <tr key={c.objectId} onClick={() => selectCourse(c)}
                            style={{ borderBottom:`1px solid ${C.border}`, cursor:"pointer", transition:"background .1s" }}
                            onMouseEnter={e => e.currentTarget.style.background = C.hover}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                          >
                            <td style={{ padding:"14px 16px", fontSize:12, color:C.t3, fontWeight:600, width:36 }}>{i+1}</td>
                            <td style={{ padding:"14px 16px" }}>
                              <div style={{ fontSize:14, fontWeight:700, color:C.t1 }}>{c.courseName||"—"}</div>
                            </td>
                            <td style={{ padding:"14px 16px" }}>
                              <span style={{ fontSize:12, fontWeight:600, padding:"3px 10px", borderRadius:20, background:C.accentBg, color:C.accentTxt, whiteSpace:"nowrap" }}>{c.program||"—"}</span>
                            </td>
                            <td style={{ padding:"14px 16px", fontSize:13, color:C.t1 }}>
                              {(() => {
                                const stat = codeStats[c.objectId];
                                if (!stat) return <span style={{ color:C.t3 }}>—</span>;
                                return <span><span style={{ fontWeight:700, color: stat.used > 0 ? C.accentTxt : C.t3 }}>{stat.used}</span><span style={{ color:C.t3 }}> / {stat.total}개</span></span>;
                              })()}
                            </td>
                            <td style={{ padding:"14px 16px", fontSize:12, color:C.t2 }}>{c.useDate||"—"}</td>
                            <td style={{ padding:"14px 16px", fontSize:12, color:C.t2 }}>{c.round||"—"}회차</td>
                            <td style={{ padding:"14px 16px", fontSize:12, color:C.t2 }}>{c.duration||"—"}분</td>
                            <td style={{ padding:"14px 16px" }} onClick={e => e.stopPropagation()}>
                              <Btn variant="danger" onClick={e => delCourse(c, e)} style={{ padding:"5px 12px", fontSize:12 }}>삭제</Btn>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ padding:"10px 0", fontSize:12, color:C.t3 }}>총 {filtered.length}개 과정</div>
                </div>
              </>
            )}

            {page === "report" && <ReportPage courses={courses}/>}
          </div>

          {/* 상세 패널 */}
          <div style={{ position:"absolute", inset:0, transform:panelOpen?"translateX(0)":"translateX(100%)", transition:"transform .28s cubic-bezier(.4,0,.2,1)", zIndex:10 }}>
            {panelOpen && <DetailPanel course={sel} onClose={closePanel} toast={toast}/>}
          </div>
        </div>
      </div>

      {showAdd && (
        <AddCourseModal
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
          toast={toast}
          existingCourses={courses}
        />
      )}

      <ToastContainer/>
      <style>{`@keyframes adm-slideUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}